import { Form, Row, Col, Card, FormInstance, Upload, Modal, message, Tag, Tooltip, Progress, Empty, Alert, Typography, Button } from 'antd';
import {
  UploadOutlined,
  EyeOutlined,
  DeleteOutlined,
  CheckCircleFilled,
  ExclamationCircleFilled,
  InboxOutlined,
  FilePdfOutlined,
  FileImageOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import ParamsPicklist from '../Types/ParamsPicklist';
import Datas from "../Data/Data.json";
import { useAuthHeader, useAuthUser } from '../Services/Auth/sessionAuth';
import { Crypter } from '../Functions/Security/Crypter';
import axios from 'axios';
import { useState, useEffect } from 'react';

// -------- Types --------

interface DocumentDefinition {
  CodeProcessus: string;
  CodeEtape: string;
  TypeCodeDoc: string;
  LibelleDoc: string;
  EstObligatoire: boolean;
  OrdreAffichage: number;
  LibelleProcessus?: string;
  TailleMax?: number | null; // en Ko
}

// Correspond EXACTEMENT aux colonnes de la table SQL Documents
export interface UploadedDocument {
  // Rattachement polymorphe (renseigné plus tard par le formulaire parent au submit)
  TypeEntiteLiee: 'CLIENT' | 'PROCURATION' | 'FINANCEMENT' | null;
  IdEntiteLiee: string | null;

  // Contexte métier
  CodeProcessus: string;
  CodeEtape: string;
  NumeroEtapeWorkflow: number | null;
  TypeCodeDoc: string;

  // Métadonnées du fichier
  NomFichier: string | null;
  ExtensionFichier: string | null;
  TailleFichier: number | null;   // en Ko
  FichierBinaire: string | null;  // base64

  // États de validation
  EstSigneClient: boolean;
  EstSigneBanque: boolean;
  EstVerifieConformite: boolean;
  DateExpiration: string | null;

  // Traçabilité
  StatutDoc: 'CHARGE' | 'VALIDE' | 'REJETE' | 'EXPIRE';
  MotifRejet: string | null;
  CreePar: string | null;
  DateCreation: string | null;
  ModifiePar: string | null;
  DateModification: string | null;

  // Champs d'affichage uniquement (non persistés en base)
  LibelleDoc: string;
  EstObligatoire: boolean;
  OrdreAffichage: number;
  TailleMax?: number | null;
}

interface Props {
  form: FormInstance;
  disabled?: boolean;
  Size?: any;
  label: string;
  name: string;
  width?: number;
  widthl?: number;
  Option?: DocumentDefinition[];
  onchange?: any;
  notRequired?: true;
  Parametre?: ParamsPicklist;
  DefaulValue?: UploadedDocument[];
  defaultMaxSizeKo?: number; // utilisé si TailleMax est NULL, défaut 2000 Ko (~2 Mo)
  accept?: string;
}

const fileToBase64 = (file: File): Promise<{ base64: string; contentType: string }> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => {
      const result = reader.result as string;
      const base64 = result.split(',')[1] || '';
      resolve({ base64, contentType: file.type });
    };
    reader.onerror = (err) => reject(err);
  });

const getExtension = (fileName: string): string => {
  const parts = fileName.split('.');
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : '';
};

const formatKo = (ko: number) => (ko >= 1024 ? `${(ko / 1024).toFixed(1)} Mo` : `${ko} Ko`);

const SetMultipleDocument = (props: Props) => {

  const notR = props.notRequired ? false : true;
  const authHeader = useAuthHeader();
  const auth = useAuthUser();
  const defaultMaxKo = props.defaultMaxSizeKo ?? 2000;

  const [Load, setLoad] = useState(false);
  const [DocsDefinition, setDocsDefinition] = useState<DocumentDefinition[]>(
    props.Option ? props.Option : []
  );
  const [previewDoc, setPreviewDoc] = useState<(UploadedDocument & { _ContentType?: string }) | null>(null);
  const [uploadingCode, setUploadingCode] = useState<string | null>(null);
  const [sizeError, setSizeError] = useState<string | null>(null);

  // -------- Valeur courante du champ (toujours sécurisée en tableau) --------
  const watchedValue = Form.useWatch(props.name, props.form);

  const CurrentValues: UploadedDocument[] = Array.isArray(watchedValue)
    ? watchedValue
    : Array.isArray(props.DefaulValue)
      ? props.DefaulValue
      : [];

  // -------- Récupération de la liste des documents attendus (contient déjà CodeProcessus/CodeEtape) --------
  const LoadDocumentsDefinition = async () => {

    setLoad(true);

    const data = {
      CodeAgence: props.Parametre?.CodeAgence,
      Table: props.Parametre?.Table,
      Colonne1: props.Parametre?.Colonne1,
      Colonne2: props.Parametre?.Colonne2,
      Colagence: props.Parametre?.Colagence,
      Init: props.Parametre?.Init,
      choix: props.Parametre?.Choix,
      colfiltre: props.Parametre?.colfiltre,
      valfiltre: props.Parametre?.valfiltre
    };

    const config = {
      method: 'get',
      maxBodyLength: Infinity,
      url: Datas.ApiUrl.AnySearch,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': authHeader(),
        'CypherSearch': Crypter.encrypt(JSON.stringify(data))
      }
    };

    await axios(config)
      .then((response) => {

        if (response.data) {

          const res = JSON.parse(
            Crypter.decrypt(response.data)
          ).Value;

          if (res.StatutCode === 200) {

            const rows: DocumentDefinition[] = res.Result.Table || [];

            const sorted = [...rows].sort(
              (a, b) => a.OrdreAffichage - b.OrdreAffichage
            );

            setDocsDefinition(sorted);
          }
        }

        setLoad(false);
      })
      .catch((error) => {
        console.log(error);
        setLoad(false);
      });
  };

  useEffect(() => {
    if (DocsDefinition.length === 0) {
      LoadDocumentsDefinition();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -------- Merge définition + valeurs déjà présentes dans le formulaire --------
  const getUploadedFor = (code: string): UploadedDocument | undefined =>
    CurrentValues.find((d) => d.TypeCodeDoc === code);

  const updateFormValue = (updated: UploadedDocument[]) => {
    props.form.setFieldsValue({ [props.name]: updated });
    props.onchange?.(updated);
  };

  // -------- Upload avec message d'erreur clair sur la taille / le format --------
  const handleUpload = async (doc: DocumentDefinition, file: File) => {

    setSizeError(null);

    const tailleKo = Math.round(file.size / 1024);
    const maxKo = doc.TailleMax ?? defaultMaxKo;

    if (tailleKo > maxKo) {
      const texte = `"${file.name}" fait ${formatKo(tailleKo)}, mais "${doc.LibelleDoc}" accepte au maximum ${formatKo(maxKo)}.`;
      message.error({ content: texte, duration: 5 });
      setSizeError(texte);
      return;
    }

    const extensionsAcceptees = (props.accept || '.pdf,.jpg,.jpeg,.png').split(',').map(e => e.trim());
    const ext = '.' + getExtension(file.name);
    if (!extensionsAcceptees.includes(ext)) {
      const texte = `Format "${ext}" non autorisé pour "${doc.LibelleDoc}". Formats acceptés : ${extensionsAcceptees.join(', ')}.`;
      message.error({ content: texte, duration: 5 });
      setSizeError(texte);
      return;
    }

    try {
      setUploadingCode(doc.TypeCodeDoc);

      const { base64, contentType } = await fileToBase64(file);

      const utilisateur = auth ? JSON.parse(JSON.stringify(auth())) : null;
      const matricule = utilisateur?.matricule || utilisateur?.username || utilisateur?.login || null;

      const newEntry: UploadedDocument = {
        // Rattachement (renseigné plus tard au submit par le parent)
        TypeEntiteLiee: null,
        IdEntiteLiee: null,

        // Contexte métier
        CodeProcessus: doc.CodeProcessus,
        CodeEtape: doc.CodeEtape,
        NumeroEtapeWorkflow: null,
        TypeCodeDoc: doc.TypeCodeDoc,

        // Métadonnées du fichier
        NomFichier: file.name,
        ExtensionFichier: getExtension(file.name),
        TailleFichier: tailleKo,
        FichierBinaire: base64,

        // États de validation (valeurs par défaut de la table)
        EstSigneClient: false,
        EstSigneBanque: false,
        EstVerifieConformite: false,
        DateExpiration: null,

        // Traçabilité
        StatutDoc: 'CHARGE',
        MotifRejet: null,
        CreePar: matricule,
        DateCreation: new Date().toISOString(),
        ModifiePar: null,
        DateModification: null,

        // Affichage uniquement
        LibelleDoc: doc.LibelleDoc,
        EstObligatoire: doc.EstObligatoire,
        OrdreAffichage: doc.OrdreAffichage,
        TailleMax: doc.TailleMax
      };

      // Gardé en mémoire uniquement pour l'aperçu (pas une colonne SQL)
      (newEntry as any)._ContentType = contentType;

      const withoutCurrent = CurrentValues.filter(
        (d) => d.TypeCodeDoc !== doc.TypeCodeDoc
      );

      updateFormValue([...withoutCurrent, newEntry]);
      message.success(`"${doc.LibelleDoc}" chargé avec succès.`);
    } catch (error) {
      console.log(error);
      message.error("Erreur lors de la lecture du fichier.");
    } finally {
      setUploadingCode(null);
    }
  };

  // -------- Suppression d'un fichier chargé --------
  const handleRemove = (doc: DocumentDefinition, e?: React.MouseEvent) => {
    e?.stopPropagation();
    const updated = CurrentValues.filter(
      (d) => d.TypeCodeDoc !== doc.TypeCodeDoc
    );
    updateFormValue(updated);
  };

  // -------- Construction des lignes --------
  const rows = DocsDefinition.map((doc) => {
    const uploaded = getUploadedFor(doc.TypeCodeDoc);
    return {
      key: doc.TypeCodeDoc,
      CodeProcessus: doc.CodeProcessus,
      CodeEtape: doc.CodeEtape,
      TypeCodeDoc: doc.TypeCodeDoc,
      LibelleDoc: doc.LibelleDoc,
      EstObligatoire: doc.EstObligatoire,
      OrdreAffichage: doc.OrdreAffichage,
      TailleMax: doc.TailleMax,
      NomFichier: uploaded?.NomFichier ?? null,
      ExtensionFichier: uploaded?.ExtensionFichier ?? null,
      TailleFichier: uploaded?.TailleFichier ?? null,
      FichierBinaire: uploaded?.FichierBinaire ?? null,
      StatutDoc: uploaded?.StatutDoc ?? null,
      _ContentType: (uploaded as any)?._ContentType ?? null
    } as UploadedDocument & { key: string; _ContentType?: string };
  });

  const totalObligatoires = DocsDefinition.filter(d => d.EstObligatoire).length;
  const chargesObligatoires = DocsDefinition.filter(
    d => d.EstObligatoire && getUploadedFor(d.TypeCodeDoc)?.FichierBinaire
  ).length;
  const progressPercent = totalObligatoires > 0
    ? Math.round((chargesObligatoires / totalObligatoires) * 100)
    : 100;

  // -------- Validation : tous les documents obligatoires doivent être chargés --------
  const validateRequiredDocs = () => {
    if (!notR) return Promise.resolve();

    const missing = DocsDefinition.filter(
      (doc) => doc.EstObligatoire && !getUploadedFor(doc.TypeCodeDoc)?.FichierBinaire
    );

    if (missing.length > 0) {
      return Promise.reject(
        new Error(
          `Documents obligatoires manquants : ${missing
            .map((d) => d.LibelleDoc)
            .join(', ')}`
        )
      );
    }

    return Promise.resolve();
  };

  const previewContentType =
    (previewDoc as any)?._ContentType ||
    (previewDoc?.ExtensionFichier === 'pdf' ? 'application/pdf' : '');

  const getFileIcon = (ext: string | null) => {
    if (ext === 'pdf') return <FilePdfOutlined style={{ fontSize: 20, color: '#d4380d' }} />;
    return <FileImageOutlined style={{ fontSize: 20, color: '#096dd9' }} />;
  };

  return (
    <>
      <Form.Item
        label={props.label}
        name={props.name}
        initialValue={props.DefaulValue}
        className='bordure'
        preserve={true}
        rules={[
          {
            validator: validateRequiredDocs
          }
        ]}
        labelCol={{
          span: props.width,
          offset: props.widthl,
          style: { whiteSpace: 'pre-wrap' }
        }}
      >
        <div style={{ background: '#fafafa', borderRadius: 12, padding: 16, border: '1px solid #f0f0f0' }}>

          {/* En-tête avec progression */}
          {totalObligatoires > 0 && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
              padding: '10px 16px',
              background: '#fff',
              borderRadius: 10,
              boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
            }}>
              <div>
                <Typography.Text strong style={{ fontSize: 14 }}>
                  Documents obligatoires
                </Typography.Text>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>
                  {chargesObligatoires} sur {totalObligatoires} chargés
                </div>
              </div>
              <Progress
                type="circle"
                percent={progressPercent}
                size={44}
                strokeColor={progressPercent === 100 ? '#52c41a' : '#1677ff'}
              />
            </div>
          )}

          {sizeError && (
            <Alert
              message={sizeError}
              type="error"
              showIcon
              closable
              onClose={() => setSizeError(null)}
              style={{ marginBottom: 16, borderRadius: 8 }}
            />
          )}

          {Load ? (
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <ReloadOutlined spin style={{ fontSize: 24, color: '#1677ff' }} />
              <div style={{ marginTop: 8, color: '#8c8c8c' }}>Chargement des documents requis...</div>
            </div>
          ) : rows.length === 0 ? (
            <Empty description="Aucun document requis" />
          ) : (
            <Row gutter={[16, 16]}>
              {rows.map((record) => {
                const isLoaded = !!record.FichierBinaire;
                const isUploading = uploadingCode === record.TypeCodeDoc;

                return (
                  <Col xs={24} sm={12} lg={8} key={record.key}>
                    <Card
                      size="small"
                      style={{
                        borderRadius: 10,
                        borderLeft: `4px solid ${isLoaded ? '#52c41a' : record.EstObligatoire ? '#ff7875' : '#d9d9d9'}`,
                        boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
                        height: '100%'
                      }}
                      bodyStyle={{ padding: 14 }}
                    >
                      {/* Titre du document */}
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                        <Typography.Text
                          strong
                          style={{ fontSize: 13, lineHeight: '18px', flex: 1, marginRight: 8 }}
                        >
                          {record.LibelleDoc}
                        </Typography.Text>
                        {isLoaded ? (
                          <CheckCircleFilled style={{ color: '#52c41a', fontSize: 18 }} />
                        ) : record.EstObligatoire ? (
                          <ExclamationCircleFilled style={{ color: '#ff7875', fontSize: 18 }} />
                        ) : null}
                      </div>

                      <div style={{ marginBottom: 10 }}>
                        {record.EstObligatoire && (
                          <Tag color="red" style={{ fontSize: 11, marginBottom: 4 }}>Obligatoire</Tag>
                        )}
                        <Tag color="default" style={{ fontSize: 11 }}>
                          Max {formatKo(record.TailleMax ?? defaultMaxKo)}
                        </Tag>
                      </div>

                      {/* Zone fichier chargé OU zone de dépôt */}
                      {isLoaded ? (
                        <div
                          onClick={() => setPreviewDoc(record)}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 10,
                            padding: '10px 12px',
                            background: '#f6ffed',
                            border: '1px solid #b7eb8f',
                            borderRadius: 8,
                            cursor: 'pointer',
                            transition: 'all 0.2s'
                          }}
                        >
                          {getFileIcon(record.ExtensionFichier)}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{
                              fontSize: 12,
                              fontWeight: 500,
                              whiteSpace: 'nowrap',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis'
                            }}>
                              {record.NomFichier}
                            </div>
                            <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                              {formatKo(record.TailleFichier || 0)}
                            </div>
                          </div>
                          <Tooltip title="Voir le document">
                            <Button
                              type="primary"
                              size="small"
                              shape="circle"
                              icon={<EyeOutlined />}
                              onClick={(e) => { e.stopPropagation(); setPreviewDoc(record); }}
                            />
                          </Tooltip>
                          <Tooltip title="Supprimer">
                            <Button
                              danger
                              size="small"
                              shape="circle"
                              icon={<DeleteOutlined />}
                              disabled={props.disabled}
                              onClick={(e) => handleRemove(record, e)}
                            />
                          </Tooltip>
                        </div>
                      ) : (
                        <Upload.Dragger
                          showUploadList={false}
                          disabled={props.disabled}
                          accept={props.accept || '.pdf,.jpg,.jpeg,.png'}
                          beforeUpload={(file) => {
                            handleUpload(record, file);
                            return false;
                          }}
                          style={{
                            padding: '10px 4px',
                            borderRadius: 8,
                            background: '#fff',
                            borderColor: '#d9d9d9'
                          }}
                        >
                          {isUploading ? (
                            <div style={{ padding: '6px 0' }}>
                              <ReloadOutlined spin style={{ fontSize: 18, color: '#1677ff' }} />
                              <div style={{ fontSize: 11, marginTop: 4, color: '#8c8c8c' }}>Chargement...</div>
                            </div>
                          ) : (
                            <div style={{ padding: '6px 0' }}>
                              <InboxOutlined style={{ fontSize: 22, color: '#bfbfbf' }} />
                              <div style={{ fontSize: 11, marginTop: 4, color: '#8c8c8c' }}>
                                Cliquez ou glissez le fichier ici
                              </div>
                            </div>
                          )}
                        </Upload.Dragger>
                      )}

                      {isLoaded && (
                        <Upload
                          showUploadList={false}
                          disabled={props.disabled}
                          accept={props.accept || '.pdf,.jpg,.jpeg,.png'}
                          beforeUpload={(file) => {
                            handleUpload(record, file);
                            return false;
                          }}
                        >
                          <Button
                            type="link"
                            size="small"
                            icon={<UploadOutlined />}
                            loading={isUploading}
                            disabled={props.disabled}
                            style={{ padding: 0, marginTop: 8, fontSize: 12 }}
                          >
                            Remplacer le fichier
                          </Button>
                        </Upload>
                      )}
                    </Card>
                  </Col>
                );
              })}
            </Row>
          )}
        </div>
      </Form.Item>

      {/* Aperçu bien visible en grand format */}
      <Modal
        open={!!previewDoc}
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {getFileIcon(previewDoc?.ExtensionFichier ?? null)}
            <span>{previewDoc?.LibelleDoc}</span>
          </div>
        }
        onCancel={() => setPreviewDoc(null)}
        footer={[
          <Button key="close" onClick={() => setPreviewDoc(null)}>
            Fermer
          </Button>
        ]}
        width={820}
        centered
        bodyStyle={{ padding: 0, background: '#f5f5f5', borderRadius: '0 0 8px 8px' }}
      >
        <div style={{
          minHeight: 500,
          maxHeight: '75vh',
          overflow: 'auto',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 16
        }}>
          {previewDoc?.FichierBinaire && previewContentType.startsWith('image/') && (
            <img
              src={`data:${previewContentType};base64,${previewDoc.FichierBinaire}`}
              alt={previewDoc.LibelleDoc}
              style={{ maxWidth: '100%', maxHeight: '70vh', borderRadius: 6, boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
            />
          )}

          {previewDoc?.FichierBinaire && previewContentType === 'application/pdf' && (
            <iframe
              src={`data:application/pdf;base64,${previewDoc.FichierBinaire}`}
              title={previewDoc.LibelleDoc}
              style={{ width: '100%', height: '70vh', border: 'none', borderRadius: 6, background: '#fff' }}
            />
          )}

          {previewDoc?.FichierBinaire &&
            !previewContentType.startsWith('image/') &&
            previewContentType !== 'application/pdf' && (
              <Empty description={`Aperçu non disponible pour ${previewDoc.NomFichier}`} />
            )}
        </div>
      </Modal>
    </>
  );
};

export default SetMultipleDocument;